import { memo, useMemo } from "react";
import {
  Stack,
  styled, Typography, debounce
} from "@mui/material";
import {
  ChartsReferenceLine,
  LineChart,
  lineElementClasses,
  markElementClasses,
  useDrawingArea,
  useXScale,
  useYScale
} from "@mui/x-charts";
import { green, orange, red } from "@mui/material/colors";
import "../InventoryDetail.css";
import { ConditionalWrapper } from "../../../../Components/utils/ConditionalWrapper";
import { useHighlightMarkIdx } from "./InventoryGraphView";
import { getProjectionDotColor } from "../../../../utils/colorUtils";

export const RenderChart = memo(({
  isLoading,
  xSeries,
  ySeries,
  height,
  width
}) => {
  const [min, projections, max] = ySeries;
  const isEmpty = ySeries.some(y => y.data.length === 0)
  const otherProps = {
    loading: !isLoading.data && !isLoading.error,
    yAxis: [
      isEmpty ? { min: -3000, max: 3000 } : { scaleType: 'linear', id: 'projections', width: 500 }
    ],
    series: isEmpty ? [] : ySeries,
    margin: { top: 20, bottom: 50, left: 185 }
  }

  return (
    <Stack direction="column" width="100%" spacing={1} padding={1}>
      <LineChart
        {...otherProps}
        xAxis={[
          {
            data: xSeries, scaleType: "point", tickLabelStyle: {
              height: 12
            }
          },
        ]}
        height={height}
        width={Math.max(136 * xSeries.length, width)}
        sx={{
          [`.${lineElementClasses.root}`]: {
            strokeWidth: 3,
          },
          [`.${markElementClasses.root}`]: {
            strokeWidth: 2
          },
          [`.${markElementClasses.root}:not(.${markElementClasses.highlighted})`]: {
            fill: '#fff',
          },
          [`& .${markElementClasses.highlighted}`]: {
            stroke: 'none',
          },
        }}
        slots={{
          mark: (props) => (
            <CustomMark
              {...props}
              skipAnimation={false}
              projections={projections.data}
              min={min.data}
              max={max.data}
            />
          ),
          loadingOverlay: LoadingOverlay
        }}
        slotProps={{
          legend: {
            axisDirection: 'y',
            direction: 'column',
            markType: 'square',
            labelPosition: 'inline-end',
            position: {
              horizontal: 'left',
              vertical: 'middle'
            },
            seriesToDisplay: [
              {
                id: 'minstock',
                label: 'Threshold',
                color: orange[500],
              }
            ]
          },
          // loadingOverlay: { message: 'Fetching Data... should be available soon.', x: '500' },
          noDataOverlay: { message: 'No data available.', x: '700' },
        }}
      >
        <ConditionalWrapper condition={!isEmpty}>
          <ChartsReferenceLine y={0} />
        </ConditionalWrapper>
      </LineChart>
      <Typography align="center">
        Projections
      </Typography>
    </Stack>
  )
})

const CustomMark = memo((props) => {
  const { x, y, dataIndex, projections, min, max } = props;
  const proj = projections[dataIndex];
  const minVal = min[dataIndex];
  const maxVal = max[dataIndex] + min[dataIndex];
  const { highlightMarkIdx, setHighlightMarkIdx } = useHighlightMarkIdx()

  // create a stable debounced setter so we don't recreate debounce on every render
  const debouncedSet = useMemo(() => debounce((idx) => setHighlightMarkIdx(idx), 300), [setHighlightMarkIdx]);

  const color = getProjectionDotColor(Number(proj), minVal, maxVal);

  return (
    <circle
      cx={x}
      cy={y}
      r={5}
      fill={color}
      stroke={color}
      strokeWidth={2}
      className={[
        highlightMarkIdx === dataIndex ? markElementClasses.highlighted : "",
        highlightMarkIdx !== dataIndex && highlightMarkIdx >= 0 ? markElementClasses.faded : ""
      ].join(" ").trim()}
      onMouseEnter={() => debouncedSet(dataIndex)}
      onMouseLeave={() => setHighlightMarkIdx(-1)}
    />
  );
})

const LoadingOverlay = () => {
  const xScale = useXScale();
  const yScale = useYScale();
  const { height } = useDrawingArea();
  const bandWidth = '136px';
  const [bottom, top] = yScale.range();
  const ratios = [0.3, 0.6, 0.4, 0.7, 0.8, 0.5, 0.2, 0.9, 0.4, 0.6];

  return (
    <g>
      {xScale.domain().map((item, index) => {
        const ratio = ratios[index % ratios.length];
        const barHeight = ratio * (bottom - top);

        return (
          <LoadingRect
            key={index}
            x={xScale(item)}
            width={bandWidth}
            y={bottom - barHeight}
            height={height}
          />
        );
      })}
      <LoadingText x={700} y={top + height / 2}>
        Loading data…
      </LoadingText>
    </g>
  );
}

const LoadingRect = styled('rect')({
  opacity: 0.2,
  fill: 'lightgray',
  animation: 'loadingAnimation 1.75s infinite',
  '@keyframes loadingAnimation': {
    '0%': { opacity: 0.2 },
    '50%': { opacity: 0.6 },
    '100%': { opacity: 0.2 },
  }
});

const LoadingText = styled('text')(({ theme }) => ({
  stroke: 'none',
  fill: theme.palette.text.primary,
  shapeRendering: 'crispEdges',
  textAnchor: 'middle',
  dominantBaseline: 'middle',
}));